(ns main.views
  (:require
   [reagent.core :as r]
   [very.re-frame.core :refer [<sub >evt] :as rf]
   [oops.core :refer [oset!]]

   [error-boundary.views :refer [error-boundary]]
   [css.core :as css
    :refer [class-names get-theme]
    :refer-macros [defstyled]]
   [css.form :as form]
   [css.icons :as icon]
   [css.vars :as vars]
   [css.typography :as typo]
   [cljss.core :as cljss]
   [main.common :as common]
   [modal.views :refer [modal-container]]
   [notification.views :as notification]
   [authentication.views :as authentication]
   [impersonate.views :as impersonate]
   [dashboard.views :as dashboard]
   [accounts.views :as accounts]
   [client-login.views :as client-login]
   [guidebook.views :as guidebook]
   [profile.views :as profile]
   [client-onboard.views :as onboard]
   [loader.views :as loader]
   [branding.views :as branding]
   [util.core :refer [abbrev-full-name]])
  (:require-macros
   [logging.core :refer [log]]))

(def DEBUG-error-boundary? (r/atom nil))
(oset! js/window
       "!very.!_debug.!main.!error_boundary"
       (fn [v]
         (reset! DEBUG-error-boundary? v)
         (r/force-update-all)))

(defstyled controls-grid-s :div
  {:display "grid"
   :background (with-meta #(if % vars/black "none") :impersonating?)
   :grid-template-columns "[left-gutter-start] minmax(1rem, 1fr) [left-gutter-end content-start] minmax(min-content, 82rem) [content-end right-gutter-start] minmax(1rem, 1fr) [right-gutter-end]"})

(defstyled account-controls-s :ol
  {:align-items "center"
   :display "flex"
   :font-size "0.87rem"
   :justify-content "flex-end"
   :margin "0.75rem 0"
   :grid-column "content"})

(defstyled account-controls-li-s :li
  {:border-left (str "1px solid " (get-theme :primary))
   :margin-left ".5rem"
   :padding-left ".5rem"
   :&:first-child {:color "rgb(176, 176, 176)"
                   :border "none"
                   :margin "0"
                   :padding "0"}})

(defstyled account-controls-link-s :a
  {:cursor "pointer"
   :color (with-meta #(if % "white" (get-theme :primary)) :impersonating?)})

(defstyled impersonating-text-s :div
  {:color "white"
   :display "grid"
   :grid-template-columns "max-content max-content"
   :padding ".75rem 0"})

(defstyled controls-content-s :div
  {:grid-column "content"
   :display "flex"
   :justify-content (with-meta #(if % "space-between" "flex-end") :impersonating?)
   :align-items "center"})

(defstyled client-image-s :img
  {:width "1.75rem"
   :height "1.75rem"
   :margin-left "1.5rem"
   :border-radius "100%"})

(defn account-controls []
  (let [impersonating? (rf/subscribe [:impersonate/token])
        personal (rf/subscribe [:personal-information])]
    (fn [client]
      [controls-grid-s {:impersonating? @impersonating?}
       [controls-content-s {:impersonating? @impersonating?}
        (when @impersonating?
          [impersonating-text-s {:data-test "impersonating-header"}
           [:span "Impersonating "]
           [common/bold-s {:padding "0 0 0 .25rem"} (:name client)]])
        [account-controls-s
         [account-controls-li-s
          [account-controls-link-s {:on-click #(rf/>evt [:navigate-to-context "/client-login"])
                                    :impersonating? @impersonating?} "Client Login"]]
         [account-controls-li-s
          [account-controls-link-s {:on-click #(rf/>evt [:log-out])
                                    :impersonating? @impersonating?} "Log Out"]]
         (when (and @impersonating? (:photo-url @personal))
           [client-image-s {:src (:photo-url @personal)}])]]])))

(defstyled menu-navigation-overlay-s :div
  {::cljss/media {[[:max-width "900px"]]
                  {:display (with-meta #(if % "block" "none") :open?)
                   :position "fixed"
                   :top "0"
                   :left "0"
                   :right "0"
                   :bottom "0"
                   :background "rgba(0,0,0,.35)"
                   :z-index "98"
                   :cursor "pointer"}}})

(defstyled menu-navigation-s :nav
  {:display "grid"
   :grid-template-columns "1fr 2fr"
   :padding "1rem 0"
   :grid-column "content"})

(defstyled menu-navigation-links-s :div
  {:display "grid"
   :grid-column-gap "2rem"
   :grid-template-columns "repeat(3, 1fr)"
   :justify-self "end"
   ::cljss/media {[[:max-width "900px"]]
                  {:grid-template-columns "1fr"
                   :position "fixed"
                   :right (with-meta #(if % "0" "-400px") :open?)
                   :top "0"
                   :transition "all .25s"
                   :bottom "0"
                   :display "flex"
                   :flex-direction "column"
                   :background "white"
                   :z-index "99"
                   :width "320px"}}})

(defstyled menu-navigation-link-s :a
  {:align-self "center"
   :border-bottom (with-meta #(if %
                                (str "1px solid " (get-theme :primary))
                                "1px solid transparent")
                    :active?)
   :color vars/text
   :display "flex"
   :align-items "center"
   :font-size "0.93rem"
   :justify-content "center"
   :padding "1rem 0.25rem"
   :text-decoration "none"
   :height "57px"
   :transition "border .25s ease-in"
   :text-transform "uppercase"
   :&:hover {:border-bottom (str "1px solid " (get-theme :primary))
             :color (get-theme :primary)}
   :&:focus {:outline "none"}
   ::cljss/media {[[:max-width "900px"]]
                  {:width "100%"
                   :justify-content "flex-start"
                   :padding "2rem 1rem"
                   :border-bottom "1px solid #ddd"
                   :font-weight (with-meta #(if % "bold" "normal") :active?)
                   :color (with-meta #(if % (get-theme :primary) "inherited") :active?)
                   :font-size "1rem"
                   :height "auto"}}})

(defstyled icon-s :i
  {:margin-right "0.5rem"
   ::cljss/media {[[:max-width "900px"]]
                  {:margin-right "1rem"
                   :width "25px"
                   :display "flex"
                   :align-items "center"
                   :justify-content "center"}}})

(defn menu-icon [arg]
  (condp = arg
    "dashboard" [icon/dashboard]
    "accounts" [icon/chart]
    "profile" [icon/user]))

(defstyled mobile-toggle-s :div
  {:display "none"
   ::cljss/media {[[:max-width "900px"]]
                  {:display "flex"
                   :align-items "center"
                   :justify-content "flex-end"}}})

(defstyled mobile-close-s :div
  {:display "none"
   ::cljss/media {[[:max-width "900px"]]
                  {:display "flex"
                   :justify-content "flex-end"
                   :padding "1rem 1rem 0"}}})

(defstyled menu-nav-grid-s :div
  {:grid-template-columns "[left-gutter-start] minmax(1rem, 1fr) [left-gutter-end content-start] minmax(min-content, 82rem) [content-end right-gutter-start] minmax(1rem, 1fr) [right-gutter-end]"
   :display "grid"
   :border-top (str "1px solid " vars/gray-light)
   :border-bottom (str "1px solid " vars/gray-light)})

(defn menu-navigation []
  (let [items {:dashboard {:icon "dashboard"}
               :accounts {:icon "accounts"}
               :profile {:icon "profile"}}
        show-mobile? (r/atom false)]
    (fn [ctx]
      [menu-nav-grid-s
       [menu-navigation-s {:type "list"}
        [branding/logo]
        [menu-navigation-links-s {:open? @show-mobile?}
         [mobile-close-s [icon/close {:cursor "pointer" :on-click #(swap! show-mobile? not) :width "25px" :height "25px"}]]
         (for [[kw {:keys [icon]}] items
               :let [link (name kw)]]
           [menu-navigation-link-s
            {:data-test (str "menu-navigation-" link)
             :key link
             :href "javascript:void(0);"
             :active? (= kw ctx)
             :on-click #(>evt [:navigate-to-context (str "/" link)])}
            [icon-s (menu-icon icon)]
            link])]
        [mobile-toggle-s [icon/hamburger {:cursor "pointer" :on-click #(swap! show-mobile? not) :width "25px" :height "25px"}]]
        [menu-navigation-overlay-s {:open? @show-mobile? :on-click #(swap! show-mobile? not)}]]])))

(defstyled active-context-s :div
  {:display "grid"
   :grid-row "body"
   :grid-template-columns "[left-gutter-start] minmax(1rem, 1fr) [left-gutter-end content-start] minmax(min-content, 82rem) [content-end right-gutter-start] minmax(1rem, 1fr) [right-gutter-end]"})

(defn active-context [ctx]
  [active-context-s
   (condp = ctx
     :dashboard [dashboard/dashboard]
     :accounts [accounts/accounts]
     :profile [profile/profile]
     :client-login [client-login/client-login]
     [:div "on noooo we dont know this context"])])

(defstyled authenticated-s :div
  {:display "grid"
   :min-height "100vh"
   :grid-template-rows "[header-start] min-content [header-end body-start] auto [body-end footer-start] minmax(1rem, max-content) [footer-end]"})

(defstyled authenticated-header-s :header
  {:grid-row "header"})

(defstyled authenticated-footer-s :footer
  {:display "grid"
   :grid-template-columns vars/container-grid
   :background-color (get-theme :tertiary)
   :padding "1rem 0"
   :margin-top "8rem"})

(defstyled unauth-body-s :div
  {:max-width "82rem"
   :margin "0 auto 5rem"
   :padding "0 1rem"
   :justify-content "center"
   :display "flex"
   :flex-direction "column"
   :align-items "center"})

(defstyled full-page-container-s :div
  {:display "flex"
   :height "100%"
   :justify-content "center"})

(defn- authenticating []
  [full-page-container-s
   [unauth-body-s
    [branding/logo]
    [typo/h1 {:margin "5rem 0 0"} "Authenticating..."]]])

(defn- authenticated []
  (let [route (rf/subscribe [:route])
        client (rf/subscribe [:client])]
    (fn []
      [authenticated-s
       [loader/page]
       [modal-container]
       [notification/container]
       [authenticated-header-s
        [account-controls @client]
        [menu-navigation @route]]
       [active-context @route]
       [authenticated-footer-s
        [branding/footer]]])))

(defn- global-loader []
  (let [loader (rf/subscribe [:application-loader])]
    (fn [loaded-renderer]
      (let [ld (:loaded? @loader)]
        (if (false? ld)
          [loader/global {:error? (:fetch-error? @loader)
                          :on-continue #(rf/>evt [:continue-with-stale])
                          :on-retry #(rf/>evt [:retry-latest])}]
          [loaded-renderer])))))

(defn- onboard-container []
  [:div
   [modal-container]
   [onboard/main]])

(defn router []
  (let [route (rf/subscribe [:route])]
    (fn []
      (when @DEBUG-error-boundary? (seq false))
      (condp = @route
        :authenticating [global-loader authenticating]

        :unauthenticated [authentication/unauth-s {:data-test "unauthenticated-page"}
                          [authentication/unauth-header]
                          [unauth-body-s
                           [typo/h1 {:margin "5rem 0 0"} "You are logged out."]
                           [form/button-row-s
                            [form/button {:on-click #(set! js/window -location "/login")} "Login Now"]]]
                          [authentication/unauth-footer]]

        :guidebook [global-loader guidebook/main]

        :invite [authentication/invite]

        :impersonate.advisor [impersonate/advisor]
        :impersonate.guidebook [impersonate/guidebook]

        :login [authentication/login]
        :login.forgot [authentication/forgot-password]
        :login.reset [authentication/reset-password]
        :login.mfa [global-loader authentication/mfa]

        :dashboard [global-loader authenticated]
        :accounts [global-loader authenticated]
        :client-login [global-loader authenticated]
        :profile [global-loader authenticated]
        :onboard [onboard-container]

        :unavailable [:div "This is a page telling you that our services are unavailable for some reason."]

        ;; default/unmatched view
        [full-page-container-s
         [unauth-body-s
          [icon/error {:width "90px" :height "90px"}]
          [typo/h1 {:margin "3rem 0 1rem"} "404: We couldn't find what you're looking for."]
          [form/button {:on-click #(set! js/window -location "/")} "Return home"]]]))))

(defstyled root-error-s :div
  {:align-items "center"
   :display "flex"
   :flex-direction "column"
   :justify-content "center"
   :width "100%"
   :height "100%"})

(defstyled root-error-message-s :p
  {:padding-bottom "1rem"
   :font-size (with-meta #(or % "14px") :font-size)
   :margin (with-meta #(or % "0") :margin)
   :color (with-meta #(or % vars/text) :color)})

;; for dev purposes the root error should be exposed at the module so our reload
;; process can clear it out
(defonce root-error (r/atom nil))

(defn root []
  [error-boundary
   [router]
   ;; top level error capture
   [root-error-s
    [icon/error {:width "90px" :height "90px"}]
    [typo/h1 {:margin "3rem 0 1rem"} "Uh oh."]
    [root-error-message-s {:font-size "18px"} "Something in application has gone wrong and we could not recover from the error."]
    [root-error-message-s {:font-size "18px"} "Feel free to return to the application, as most errors can be resolved by simply reloading."]
    [form/button {:on-click #(.reload (.-location js/window))
                  :margin "1rem 0 4rem"}
     "Return to Application"]
    [root-error-message-s {:color vars/gray} "This incident has been reported to our engineers. They will resolve the issue as soon as possible."]]
   root-error])
